import classNames from 'classnames';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { ImageZoom } from './image-zoom';
import { colors } from 'utils/jobs';
import { roundNumberStrict } from 'utils/ui.utils';
const imageSize = (imgSize, imgWrapSize) => {
    if (imgWrapSize.width === 0 || imgWrapSize.height === 0) {
        return { width: 0, height: 0, heightPadding: 0 };
    }
    imgSize = { ...imgSize };
    imgWrapSize = { ...imgWrapSize };
    if (imgSize.width > imgWrapSize.width) {
        const ratio = imgSize.height / imgSize.width;
        imgSize.width = imgWrapSize.width;
        imgSize.height = ratio * imgSize.width;
    }
    if (imgSize.height > imgWrapSize.height) {
        const ratio = imgSize.width / imgSize.height;
        imgSize.height = imgWrapSize.height;
        imgSize.width = ratio * imgSize.height;
    }
    const width = Math.round(imgSize.width);
    const height = Math.round(imgSize.height);
    const heightPadding = Math.round((imgWrapSize.height - height) / 2);
    return {
        heightPadding,
        width,
        height
    };
};
const pointSizeMap = {
    small: '2',
    med: '6',
    large: '10'
};
const toPosition = (e, canvas) => {
    const { top, left } = canvas.getBoundingClientRect();
    return { x: Math.round(e.clientX - left), y: Math.round(e.clientY - top) };
};
let _lastMove = 0;
const Canvas = ({ width, height, updatePoints, twoPointClick, readonly, listPoints, onMouseOutPoint, onMouseOverPoint, pointSize, editIndex, highlightIndex }) => {
    const [canvasRef, setCanvasRef] = useState(null);
    const [coords, setCoords] = useState({
        x: null,
        y: null
    });
    const [points, setPoints] = useState([]);
    const onMouseMove = (e) => {
        const pos = toPosition(e, canvasRef);
        const lastMove = Date.now();
        _lastMove = lastMove;
        setTimeout(() => {
            if (lastMove === _lastMove) {
                setCoords({ x: null, y: null });
            }
        }, 5 * 1000);
        setCoords(pos);
    };
    const handleUpdatePoints = (newPoints) => {
        if (updatePoints && editIndex !== undefined && editIndex !== null) {
            updatePoints(editIndex, newPoints.map((p) => {
                return {
                    x: p.x / width,
                    y: 1 + p.y / height
                };
            }));
            setPoints(newPoints);
        }
    };
    const onMouseLeave = () => {
        setCoords({ x: null, y: null });
    };
    const onMouseClick = (e) => {
        if (twoPointClick && points.length === 1) {
            let pos = toPosition(e, canvasRef);
            pos = {
                x: roundNumberStrict(pos.x || 0 / width, 4),
                y: roundNumberStrict(pos.y || 0 / height, 4)
            };
            const fp = points[0];
            const tr = { x: pos.x, y: fp.y };
            const bl = { x: fp.x, y: pos.y };
            handleUpdatePoints([...points, tr, pos, bl]);
            return;
        }
        const pos = toPosition(e, canvasRef);
        handleUpdatePoints([
            ...points,
            {
                x: roundNumberStrict(pos.x || 0 / width, 4),
                y: roundNumberStrict(pos.y || 0 / height, 4)
            }
        ]);
    };
    useEffect(() => {
        setPoints([]);
    }, [readonly]);
    listPoints ??= [];
    if (!listPoints.length && points.length) {
        listPoints = [points];
    }
    return (React.createElement("div", { onClick: readonly ? () => true : onMouseClick, onMouseLeave: readonly ? () => true : onMouseLeave, onMouseMove: readonly ? () => true : onMouseMove, ref: (re) => {
            setCanvasRef(re);
        } },
        React.createElement("svg", { className: "highlight-svg", height: height, width: width },
            listPoints.map((pts, idx) => {
                const highlight = pts.some((p) => p.highlight);
                pts = pts.map((p) => ({
                    ...p,
                    x: width * p.x,
                    y: height * p.y
                }));
                if (pts.some((p) => Number.isNaN(p.x) || Number.isNaN(p.y))) {
                    return null;
                }
                return (React.createElement("g", { 
                    // eslint-disable-next-line react/no-array-index-key
                    key: idx, onMouseOut: () => {
                        onMouseOutPoint?.(pts, idx);
                    }, onMouseOver: () => {
                        onMouseOverPoint?.(pts, idx);
                    } },
                    React.createElement("polygon", { fill: highlight || highlightIndex === idx
                            ? colors.red
                            : colors.green, fillOpacity: "0.50", points: pts.map((p) => `${p.x},${p.y}`).join(' ') }),
                    pts.map((p) => (React.createElement("circle", { cx: p.x, cy: p.y, fill: highlight ? colors.red : colors.green, key: `${p.x}${p.y}`, r: pointSizeMap[pointSize] })))));
            }),
            !readonly && coords.x && coords.y ? (React.createElement("line", { style: { stroke: colors.red, strokeWidth: 2 }, x1: "0", x2: width, y1: coords.y, y2: coords.y })) : null,
            !readonly && coords.x && coords.y ? (React.createElement("line", { style: { stroke: colors.red, strokeWidth: 2 }, x1: coords.x, x2: coords.x, y1: 0, y2: height })) : null,
            !readonly && coords.x !== null && coords.y !== null ? (React.createElement("circle", { cx: coords.x, cy: coords.y, fill: colors.red, r: pointSizeMap[pointSize] })) : null)));
};
const SmartImagePure = forwardRef(({ image, readonly = true, enableZoom = false, noPoints = false, degrees, pointSize, zoomSize, addHeightPadding = true, listPoints, onMouseOutPoint, onMouseOverPoint, onImageLoaded, twoPointClick = false, imageWrapDimensions, updatePoints, noImageLabel = 'Please wait for a label...', editIndex, type = '', className, highlightIndex }, ref) => {
    const dimensions = image
        ? imageSize(image.dimensions, imageWrapDimensions)
        : { width: 0, height: 0, heightPadding: 0 };
    if (!addHeightPadding) {
        dimensions.heightPadding = 0;
    }
    const originalImageDimensions = image ? image.dimensions : {};
    return (React.createElement("div", { className: classNames('smart-image', className), ref: ref }, image && dimensions.width ? (React.createElement("div", { className: "div-1" },
        React.createElement("div", { className: "div-2", style: {
                position: 'relative',
                marginTop: `${dimensions.heightPadding}px`
            } },
            React.createElement(ImageZoom, { degrees: degrees, enableZoom: enableZoom, height: dimensions.height, imageDimensions: originalImageDimensions, onImageLoaded: onImageLoaded, src: image.url, width: dimensions.width, zoomSize: zoomSize }),
            noPoints ? null : (React.createElement(Canvas, { editIndex: editIndex, height: dimensions.height, highlightIndex: highlightIndex, listPoints: listPoints, onMouseOutPoint: onMouseOutPoint, onMouseOverPoint: onMouseOverPoint, pointSize: pointSize, readonly: readonly, twoPointClick: readonly ? null : twoPointClick, updatePoints: updatePoints, width: dimensions.width })),
            type === '' ? null : (React.createElement("div", null,
                React.createElement("br", null),
                React.createElement("b", null,
                    " ",
                    type,
                    " ")))))) : (React.createElement("h4", null, noImageLabel))));
});
SmartImagePure.displayName = 'SmartImagePure';
export const SmartImage = ({ maxHeightPerc, enableZoom = false, noPoints, listPoints, image, onMouseOverPoint, onMouseOutPoint, twoPointClick, readonly, updatePoints, editIndex, type = '', degree = 0, className, highlightIndex }) => {
    const [degrees, setDegrees] = useState(degree);
    const [imageWrapDimensions, setImageWrapDimensions] = useState({
        // HACK: SmartImage doesn't work right when this isn't done, it needs to be refactored badly
        width: enableZoom ? 0 : 1,
        height: enableZoom ? 0 : 1
    });
    const imageRef = useRef(null);
    const redoDimensions = () => {
        if (!imageRef.current) {
            return;
        }
        const width = imageRef.current.clientWidth;
        setImageWrapDimensions({ height: maxHeightPerc * width || width, width });
    };
    useEffect(() => {
        setDegrees(degree);
    }, [degree]);
    useEffect(() => {
        if (enableZoom) {
            setTimeout(() => {
                redoDimensions();
            }, 10);
        }
        window.addEventListener('resize', redoDimensions);
        return () => {
            window.removeEventListener('resize', redoDimensions);
        };
    }, []);
    const onImageLoadedHandle = () => {
        setTimeout(redoDimensions, 10);
    };
    return (React.createElement(SmartImagePure, { className: className, degrees: degrees, editIndex: editIndex, enableZoom: enableZoom, highlightIndex: highlightIndex, image: image, imageWrapDimensions: imageWrapDimensions, listPoints: listPoints, noPoints: noPoints, onImageLoaded: onImageLoadedHandle, onMouseOutPoint: onMouseOutPoint, onMouseOverPoint: onMouseOverPoint, pointSize: "small", readonly: readonly, ref: imageRef, twoPointClick: twoPointClick, type: type, updatePoints: updatePoints, zoomSize: 2 }));
};
